import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocale } from '../context/DynamicLocaleProvider';
import { APP_LANGUAGES, getLanguageByCode } from '../utils/languages';
import styles from './LanguageSwitch.module.css';

export function LanguageSwitch() {
  const { locale, setLocale, t } = useLocale();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  const current = getLanguageByCode(locale) ?? APP_LANGUAGES[0]!;
  const label = t('lang_switch_label');

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const handleSelect = (code: string) => {
    if (code !== locale) {
      void setLocale(code);
    }
    close();
  };

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        close();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
        toggleRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [close, open]);

  return (
    <div
      ref={rootRef}
      className={`${styles.langSwitch} ${open ? styles.isOpen : ''}`}
      aria-label={label}
    >
      <button
        ref={toggleRef}
        type="button"
        className={styles.toggle}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((value) => !value)}
      >
        <span className={styles.flag} aria-hidden="true">
          {current.flag}
        </span>
        <span className={styles.code}>{current.code.toUpperCase()}</span>
        <svg className={styles.chevron} width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open ? (
        <div className={styles.menu} role="listbox" aria-label={label}>
          {APP_LANGUAGES.map((lang) => {
            const isActive = lang.code === locale;
            return (
              <button
                key={lang.code}
                type="button"
                role="option"
                aria-selected={isActive}
                className={`${styles.option} ${isActive ? styles.optionActive : ''}`}
                onClick={() => handleSelect(lang.code)}
              >
                <span className={styles.flag} aria-hidden="true">
                  {lang.flag}
                </span>
                <span className={styles.name}>{lang.name}</span>
                <span className={styles.optionCode}>{lang.code.toUpperCase()}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
